import React, { useEffect, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { fetchAddressbookContact, clear, createAddressbookContact, updateAddressbookContact } from '#action/addressbook/addressbook-contact';
import { fetchAddressbooks, clear as clearAddressbooks } from '#action/addressbook/addressbooks';
import { updateBreadcrumb } from '#action/breadcrumb';
import PreloaderComponent from '#component/preloader';
import { DEFAULT_DISTANCE_DEVIATION } from '#component/shipment/form/delivery/distance-deviation';
import Form from './form';
import { useError } from '#hooks';
import { setDocumentTitle, formattedAddress, convertDistance } from '#helper';

/**
 * Renders addressbook contact form.
 * Prepares initial form values.
 * @returns {React.FC}
 */

const ContactForm = props => {
  const { match: { params: { id } }, history, location } = props;
  const dispatch = useDispatch();
  const { status, contact, error } = useSelector(s => s.addressbookContact, shallowEqual);
  const {
    addressbooks,
    status: addressbooksStatus,
    error: addressbooksError
  } = useSelector(s => s.addressbooks, shallowEqual);
  const measurementSystem = useSelector(s =>
    s.account && s.account.measurementSystem, shallowEqual);
  const loading = id && status === 'loading';
  const addressbooksLoading = addressbooksStatus === 'loading';
  const pageDescription = id ? 'Edit contact' : 'Create contact';
  const queryParams = new URLSearchParams(location.search);
  const addressbookId = queryParams.get('addressbookId');

  useError(error, addressbooksError);

  useEffect(() => {
    if (id) {
      setDocumentTitle('Edit contact', `${contact.firstName} ${contact.lastName}`);
    } else {
      setDocumentTitle('Create contact');
    }
    dispatch(updateBreadcrumb([
      { label: 'Address book', path: '/addressbook' },
      { label: pageDescription }
    ]));
  }, [contact.firstName, contact.lastName, dispatch, id, pageDescription]);

  useEffect(() => {
    if (id) {
      dispatch(fetchAddressbookContact(id));
    }
    dispatch(fetchAddressbooks());

    return () => {
      dispatch(clear());
      dispatch(clearAddressbooks());
    };
  }, [dispatch, id]);

  const handleFormSubmit = async values => {
    const transformedValues = {
      companyName: values.companyName || null,
      firstName: values.firstName || null,
      lastName: values.lastName || null,
      email: values.email || null,
      addressbookId: values.addressbook?.id || null,
      locations: values.locations?.length ?
        [{ ...values.locations[0], radius: convertDistance(+values.radius, measurementSystem, 'metric') }] : undefined,
      timedelta: values.minutes * 1000 * 60 || null,
      address1: values.address.address1 || null,
      address2: values.address.address2 || null,
      city: values.address.city || null,
      state: values.address.state || null,
      country: values.address.country || null,
      zipCode: values.address.zipCode || null
    };
    if (id) {
      await dispatch(updateAddressbookContact(id, transformedValues));
    } else {
      await dispatch(createAddressbookContact(transformedValues));
    }
    history.push('/addressbook');
  };

  const initialValues = useMemo(() => {
    const values = {
      companyName: contact.companyName ?? '',
      firstName: contact.firstName ?? '',
      lastName: contact.lastName ?? '',
      email: contact.email ?? '',
      addressbook: contact.addressbook ?? '',
      locations: contact.locations ?? [],
      radius: contact.locations?.[0]?.radius ?
        convertDistance(contact.locations[0].radius, 'metric', measurementSystem) :
        convertDistance(DEFAULT_DISTANCE_DEVIATION, 'metric', measurementSystem),
      minutes: contact.timedelta ? `${contact.timedelta / 1000 / 60}` : '60',
      address: {
        address1: contact.address1 ?? '',
        address2: contact.address2 ?? '',
        city: contact.city ?? '',
        state: contact.state ?? '',
        country: contact.country ?? '',
        zipCode: contact.zipCode ?? ''
      }
    };

    values.formattedAddress = Object.values(values.address).some(Boolean) ? formattedAddress(values.address) : '';

    const preselectedAddressbook = addressbooks.find(ab => ab.id === Number(addressbookId));

    if (!id && preselectedAddressbook) {
      values.addressbook = preselectedAddressbook;
    }

    return values;
  }, [contact, measurementSystem, addressbooks, addressbookId, id]);

  if (loading || addressbooksLoading) {
    return <PreloaderComponent />;
  }

  return (
    <Form
      {...props}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      pageDescription={pageDescription}
      measurementSystem={measurementSystem}
    />
  );
};

export default ContactForm;
